import { Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { YearMonthSegmentedNav } from '@/components/DatePicker/YearMonthSegmentedNav';
import FillScreen from '@/components/Layout/FillScreen';
import { ContractAndCostToolbar, type ContractAndCostSection } from '@/features/contract-and-cost/contract-and-cont-toolbar/ContractAndCostToolbar';
import { formatTargetMonth, getDefaultTargetMonth, parseTargetMonth, stepTargetMonth } from '@/features/contract-and-cost/targetMonth';
import { defaultNS, useTranslation } from '@/lib/i18n';
export const Route = createFileRoute('/_staff/devices/_list/contract-and-cost/_contract-and-cost')({
  component: ContractAndCostLayout
});
const TARGET_MONTH_PATH_REGEX = /\/contract-and-cost\/(payment|cost)\/(\d{6})/;
function ContractAndCostLayout() {
  return <ContractAndCostContent />;
}
function ContractAndCostContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.periodNav'
  });
  const section: ContractAndCostSection = (() => {
    const path = location.pathname;
    if (path.includes('/contract-and-cost/payment')) return 'payment';
    if (path.includes('/contract-and-cost/cost')) return 'cost';
    return 'deal';
  })();
  const rawTargetMonth = location.pathname.match(TARGET_MONTH_PATH_REGEX)?.[2];
  const currentTargetMonth = rawTargetMonth ?? getDefaultTargetMonth();
  const target = useMemo(() => parseTargetMonth(currentTargetMonth), [currentTargetMonth]);
  const handleSectionChange = useCallback((newSection: ContractAndCostSection) => {
    if (newSection === 'payment' || newSection === 'cost') {
      navigate({
        to: `/devices/contract-and-cost/${newSection}/${currentTargetMonth}`
      });
    } else {
      navigate({
        to: `/devices/contract-and-cost/${newSection}`
      });
    }
  }, [navigate, currentTargetMonth]);
  const navigateToMonth = useCallback((targetMonth: string) => {
    navigate({
      to: `/devices/contract-and-cost/${section}/${targetMonth}`,
      // 期間変更時も集計軸（カタログ別/個人別/組織別）・表示モード等の
      // 検索パラメータを維持する
      search: prev => prev
    });
  }, [navigate, section]);
  const handleMonthChange = useCallback((value: {
    year: number;
    month: number;
  } | undefined) => {
    if (value) navigateToMonth(formatTargetMonth(value.year, value.month));
  }, [navigateToMonth]);
  const showPeriodPicker = section === 'payment' || section === 'cost';
  return <FillScreen className="flex flex-col">
      <ContractAndCostToolbar value={section} onValueChange={handleSectionChange} rightContent={showPeriodPicker ? <YearMonthSegmentedNav year={target.year} month={target.month} onPrevMonth={() => navigateToMonth(stepTargetMonth(currentTargetMonth, -1))} onNextMonth={() => navigateToMonth(stepTargetMonth(currentTargetMonth, 1))} onMonthChange={handleMonthChange} prevMonthLabel={t('prevMonth')} nextMonthLabel={t('nextMonth')} selectPeriodLabel={t('selectPeriod')} /> : undefined} />
      <Outlet />
    </FillScreen>;
}